import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import IstresMoiTitle from "@/components/IstresMoiTitle";
import PageShell, { NAV_ITEMS } from "@/components/PageShell";
import VoucherMarquee from "@/components/home/VoucherMarquee";
import IstresSpotlightMap from "@/components/home/IstresSpotlightMap";
import YouTubeBackground from "@/components/YouTubeBackground";
import { api } from "@/lib/api";
import { ISTRES_LOGO } from "@/lib/istres";
import {
  ArrowRight, Newspaper, MessageCircle, Heart,
  Eye, Sparkles, Clock,
} from "lucide-react";

export default function PublicHome() {
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/posts/?featured=true&limit=3")
      .then((r) => setPosts((r.data || []).slice(0, 3)))
      .catch(() => setPosts([]))
      .finally(() => setLoadingPosts(false));
  }, []);

  return (
    <PageShell>
      {/* ============ HERO ============ */}
      <section className="relative isolate overflow-hidden" data-testid="hero-section">
        <YouTubeBackground
          videoId="7hTX4OiqPxQ"
          start={60}
          poster="https://images.unsplash.com/photo-1499002238440-d264edd596ec?auto=format&fit=crop&w=1800&q=70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-plum-900/55 via-plum-900/40 to-plum-900/65 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 70% 60% at 85% 15%, rgba(255,213,0,0.20), transparent 60%), radial-gradient(ellipse 60% 50% at 10% 100%, rgba(157,111,206,0.18), transparent 60%)",
        }} />

        <div className="relative max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28 text-center">
          <h1 className="heading-display text-white text-[2.4rem] sm:text-[4rem] lg:text-[5.5rem] leading-[1.05] sm:leading-[0.95] text-balance mx-auto max-w-4xl drop-shadow-[0_3px_24px_rgba(0,0,0,0.45)]">
            Istres, c&apos;est <span className="text-sun-300">Vous</span> !
          </h1>
          <p className="mt-6 sm:mt-7 text-cream-50/95 text-[17px] sm:text-xl lg:text-2xl max-w-2xl mx-auto font-medium leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)]">
            Entreprendre, s&apos;entraider, participer, soumettre ses idées.
            <br className="hidden sm:block" />
            <strong className="text-sun-300 font-semibold">Une appli simple pour vivre Istres ensemble.</strong>
          </p>

          <div className="mt-9 sm:mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
            <Link to="/mur" className="w-full sm:w-auto">
              <Button className="btn-pill bg-sun-400 hover:bg-sun-500 text-plum-900 h-14 px-7 text-[16px] sm:text-[17px] font-bold w-full shadow-[0_14px_40px_-10px_rgba(255,213,0,0.65)]" data-testid="hero-cta-wall">
                Voir le mur des commerçants <ArrowRight size={18} strokeWidth={2.5} />
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button className="btn-pill bg-white/12 hover:bg-white/22 text-white border border-white/30 h-14 px-6 text-[16px] backdrop-blur-xl w-full shadow-xl shadow-black/10" data-testid="hero-login-btn">
                Se connecter
              </Button>
            </Link>
          </div>

          <div className="mt-10 sm:mt-12 flex items-center justify-center gap-6 sm:gap-10 text-cream-50/80">
            <Stat number={stats?.istres_population?.toLocaleString("fr-FR") || "44 514"} label="Habitants" />
            <Divider />
            <Stat number={stats?.istres_commerces ?? 204} label="Commerces" />
            <Divider />
            <Stat number={`${stats?.platform_vouchers_active ?? 0}+`} label="Bons à gagner" />
          </div>
        </div>
      </section>

      {/* ============ ISTRES SPOTLIGHT MAP ============ */}
      <IstresSpotlightMap />

      {/* ============ MINI WALL PREVIEW ============ */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="home-wall-preview">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-100 text-pink-600 mb-3 text-[12px] font-bold uppercase tracking-wider">
              <Newspaper size={12} strokeWidth={2.5} /> Mur des commerçants
            </div>
            <h2 className="heading-display text-[2rem] sm:text-[2.6rem] text-plum-900 leading-[1.1]">
              Fil <span className="text-pink-500">d&apos;actu</span> des commerçants Istréens.
            </h2>
            <p className="mt-3 text-[16px] text-plum-700/75">
              Nouveautés, événements, coups de cœur — suivez ce qui se passe en ville en temps réel.
            </p>
          </div>
          <Link
            to="/mur"
            className="btn-pill bg-plum-700 hover:bg-plum-800 text-white px-5 h-12 text-[14px] font-bold inline-flex items-center gap-2 shrink-0"
            data-testid="home-wall-see-all"
          >
            Voir le mur complet <ArrowRight size={15} />
          </Link>
        </div>

        {loadingPosts ? (
          <div className="grid sm:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="phenix-card bg-white p-6 h-72 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="phenix-card bg-white p-10 text-center">
            <Newspaper size={28} className="mx-auto text-plum-700/30 mb-2" />
            <p className="text-[14px] text-plum-700/70">Aucun post pour le moment.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((p) => <MiniPostCard key={p.id} post={p} />)}
          </div>
        )}
      </section>

      {/* ============ QUICK ACCESS GRID ============ */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pb-12 sm:pb-16" data-testid="home-quick-access">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sun-100 text-sun-700 mb-4 text-[12px] font-bold uppercase tracking-wider">
            <Sparkles size={12} strokeWidth={2.5} /> Que voulez-vous faire ?
          </div>
          <h2 className="heading-display text-[1.8rem] sm:text-[2.4rem] text-plum-900 leading-[1.1]">
            Toutes les actions, en un clin d&apos;œil.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {QUICK_ACCESS.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className={`group relative rounded-3xl border-2 ${q.color} p-6 sm:p-7 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(15,46,81,0.18)] flex flex-col h-full`}
              data-testid={`home-quick-${q.to.replace("/", "")}`}
            >
              <div className={`w-14 h-14 rounded-2xl ${q.iconBg} text-white grid place-items-center mb-5 shadow-md`}>
                <q.icon size={24} strokeWidth={2.2} />
              </div>
              <h3 className="heading-display text-[1.2rem] sm:text-[1.4rem] text-plum-900 leading-[1.15] mb-2">
                {q.title}
              </h3>
              <p className="text-[14px] text-plum-700/75 leading-relaxed mb-4 flex-1">{q.desc}</p>
              <div className="inline-flex items-center gap-2 text-[13px] font-bold text-plum-700 group-hover:gap-3 transition-all">
                {q.cta} <ArrowRight size={14} strokeWidth={2.5} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ VOUCHERS MARQUEE ============ */}
      <VoucherMarquee />
    </PageShell>
  );
}

// ---------- Quick access tiles ----------
const QUICK_ACCESS = (() => {
  const items = NAV_ITEMS.filter((n) => n.to !== "/mur"); // Wall already featured above
  const meta = {
    "/consultations": { iconBg: "bg-plum-700",  color: "bg-plum-50 border-plum-200",  desc: "Donnez votre avis et gagnez des bons chez vos commerçants.", cta: "Voir les sondages" },
    "/idees":         { iconBg: "bg-sun-500",   color: "bg-sun-50 border-sun-200",    desc: "Une idée pour améliorer la ville ? Déposez-la ici.", cta: "Ouvrir la boîte à idées" },
    "/agenda":        { iconBg: "bg-pink-500",  color: "bg-pink-50 border-pink-200",  desc: "Marchés, concerts, promos : tout ce qui se passe à Istres.", cta: "Voir l'agenda" },
    "/plan":          { iconBg: "bg-mint-500",  color: "bg-mint-50 border-mint-200",  desc: "Commerces, parkings, défibrillateurs, marchés…", cta: "Ouvrir le plan" },
    "/covoiturage":   { iconBg: "bg-lavande-500", color: "bg-cream-100 border-lavande-200", desc: "Voyagez entre Istréens, partagez les frais.", cta: "Voir les trajets" },
    "/ressources":    { iconBg: "bg-plum-500",  color: "bg-cream-50 border-cream-200", desc: "Mairie, urgences, Office du tourisme, Istres Mag.", cta: "Voir les ressources" },
  };
  return items.map((n) => ({
    to: n.to,
    icon: n.icon,
    title: n.label,
    ...meta[n.to],
  }));
})();

function MiniPostCard({ post }) {
  const date = new Date(post.created_at);
  const fmt = date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  const hasVideo = !!post.video_url;
  const hasSurvey = !!post.attached_survey_id;
  const ev = post.attached_event;
  const hasEvent = !!ev;
  const linkTo = hasSurvey ? `/sondages/${post.attached_survey_id}` : hasEvent ? "/agenda" : "/mur";
  return (
    <Link
      to={linkTo}
      className="phenix-card bg-white overflow-hidden hover:-translate-y-0.5 transition-transform flex flex-col"
      data-testid={`home-mini-post-${post.id}`}
    >
      {(post.photo_url || hasVideo) && (
        <div className="relative aspect-[16/10] overflow-hidden bg-cream-100">
          {post.photo_url && (
            <img src={post.photo_url} alt="" className="w-full h-full object-cover" loading="lazy" />
          )}
          {hasVideo && (
            <div className="absolute inset-0 grid place-items-center bg-black/25">
              <div className="w-14 h-14 rounded-full bg-white/95 grid place-items-center shadow-xl">
                <svg viewBox="0 0 24 24" className="w-6 h-6 ml-0.5 fill-plum-700"><path d="M8 5v14l11-7z" /></svg>
              </div>
              <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-pink-500 text-white">Vidéo</span>
            </div>
          )}
          {hasSurvey && (
            <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-sun-400 text-plum-900 shadow-sm">🗳 Sondage</span>
          )}
          {hasEvent && !hasSurvey && (
            <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-plum-700 text-white shadow-sm">📅 Événement</span>
          )}
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-center gap-2 text-[11px] text-plum-700/65">
          <span className="font-display font-bold text-plum-900 text-[12px] truncate">{post.merchant_name}</span>
          {post.merchant_category && <><span>·</span><span className="truncate">{post.merchant_category}</span></>}
        </div>
        <p className="text-[13px] text-plum-900 leading-snug line-clamp-3">{post.text}</p>
        {hasSurvey && (
          <div className="mt-1 inline-flex items-center gap-2 text-[11px] font-bold text-plum-700 bg-sun-50 border border-sun-200 rounded-full px-2.5 py-1 self-start">
            <span className="text-pink-500">🎟</span> Répondez = -10% en boutique
          </div>
        )}
        {hasEvent && ev.nearest_parking && (
          <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold text-plum-700 bg-mint-50 border border-mint-200 rounded-full px-2.5 py-1 self-start">
            🅿️ {ev.nearest_parking.name} · {ev.nearest_parking.distance_m < 1000 ? `${ev.nearest_parking.distance_m} m` : `${(ev.nearest_parking.distance_m / 1000).toFixed(1)} km`}
          </div>
        )}
        <div className="mt-auto pt-2 flex items-center gap-3 text-[11px] text-plum-700/55 border-t border-cream-100">
          <span className="inline-flex items-center gap-1"><Heart size={11} /> {post.reactions?.length || 0}</span>
          <span className="inline-flex items-center gap-1"><MessageCircle size={11} /> {post.comments?.length || 0}</span>
          <span className="inline-flex items-center gap-1"><Eye size={11} /> {post.views || 0}</span>
          <span className="ml-auto inline-flex items-center gap-1"><Clock size={11} /> {fmt}</span>
        </div>
      </div>
    </Link>
  );
}

function Stat({ number, label }) {
  return (
    <div className="text-center">
      <div className="heading-display text-2xl sm:text-3xl lg:text-4xl text-white leading-none">{number}</div>
      <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-cream-50/70 mt-1.5">{label}</div>
    </div>
  );
}

function Divider() {
  return <div className="w-px h-8 bg-cream-50/25" aria-hidden="true" />;
}

function UserBadge() {
  const { user } = useAuth();
  const isAuthed = !!user && user !== false;

  if (!isAuthed) {
    return (
      <Link to="/login" className="hidden sm:block">
        <Button
          data-testid="home-cta-join"
          className="rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 h-12 text-[15px] shadow-lg shadow-plum-700/20 inline-flex items-center gap-2 font-semibold"
        >
          Se connecter <ArrowRight size={16} />
        </Button>
      </Link>
    );
  }

  const target = user.role === "admin" ? "/admin" : user.role === "commercant" ? "/commercant" : "/habitant";
  const first = user.pseudo || (user.name || "").split(" ")[0] || "Moi";
  const initial = first.trim().charAt(0).toUpperCase() || "?";
  const roleColor = user.role === "admin" ? "bg-plum-700" : user.role === "commercant" ? "bg-sun-400 text-plum-900" : "bg-pink-500";

  return (
    <Link
      to={target}
      data-testid="header-user-badge"
      className="hidden sm:inline-flex items-center gap-2 pl-1 pr-3 h-12 rounded-full bg-white hover:bg-cream-50 border border-cream-200 transition-colors shadow-sm"
    >
      <span className={`w-10 h-10 rounded-xl ${roleColor} text-white font-display font-extrabold text-[16px] grid place-items-center shrink-0`} aria-hidden="true">
        {initial}
      </span>
      <span className="flex flex-col leading-none">
        <span className="text-[9px] uppercase tracking-wider text-plum-700/60 font-bold">Bonjour</span>
        <span className="font-display font-bold text-plum-900 text-[14px] mt-0.5 truncate max-w-[120px]">{first}</span>
      </span>
    </Link>
  );
}
